<template>
  <div class="rf-billing-form">
    <h2>Name on Card</h2>
    <div class="rf-billing-section">
      <div class="rf-billing-row">
        <div class="rf-billing-field">
          <input class="rf-billing-element" type="text" placeholder="First name" />
        </div>
        <div class="rf-billing-field">
          <input class="rf-billing-element" type="text" placeholder="Last name" />
        </div>
      </div>
    </div>

    <h2>Card Information</h2>
    <div class="rf-billing-section">
      <div class="rf-billing-field">
        <div class="recurly-element-wrapper">
          <div
            class="recurly-element"
            :class="{ 'recurly-element-focus': focusedField === 'cardNumber' }"
          >
            <input
              class="recurly-hosted-field-input"
              type="tel"
              pattern="[0-9 ]*"
              spellcheck="false"
              autocapitalize="none"
              autocorrect="off"
              placeholder="1234 1234 1234 1234"
              maxlength="19"
              @focus="focusedField = 'cardNumber'"
              @blur="focusedField = null"
            />
          </div>
        </div>
      </div>
      <div class="rf-billing-row">
        <div class="rf-billing-field">
          <div class="recurly-element-wrapper">
            <div
              class="recurly-element"
              :class="{ 'recurly-element-focus': focusedField === 'cardMonth' }"
            >
              <input
                class="recurly-hosted-field-input"
                type="tel"
                pattern="[0-9]*"
                spellcheck="false"
                autocapitalize="none"
                autocorrect="off"
                placeholder="MM"
                maxlength="2"
                @focus="focusedField = 'cardMonth'"
                @blur="focusedField = null"
              />
            </div>
          </div>
        </div>
        <div class="rf-billing-field">
          <div class="recurly-element-wrapper">
            <div
              class="recurly-element"
              :class="{ 'recurly-element-focus': focusedField === 'cardYear' }"
            >
              <input
                class="recurly-hosted-field-input"
                type="tel"
                pattern="[0-9]*"
                spellcheck="false"
                autocapitalize="none"
                autocorrect="off"
                placeholder="YY"
                maxlength="2"
                @focus="focusedField = 'cardYear'"
                @blur="focusedField = null"
              />
            </div>
          </div>
        </div>
        <div class="rf-billing-field">
          <div class="recurly-element-wrapper">
            <div
              class="recurly-element"
              :class="{ 'recurly-element-focus': focusedField === 'cardCvv' }"
            >
              <input
                class="recurly-hosted-field-input"
                type="tel"
                pattern="[0-9]*"
                spellcheck="false"
                autocapitalize="none"
                autocorrect="off"
                placeholder="CVV"
                maxlength="4"
                @focus="focusedField = 'cardCvv'"
                @blur="focusedField = null"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <h2>Billing Address</h2>
    <div class="rf-billing-section">
      <div class="rf-billing-field">
        <select class="rf-billing-element">
          <option value="">Select a country</option>
          <option disabled>(Countries list not available in preview)</option>
        </select>
      </div>
      <div class="rf-billing-field">
        <input class="rf-billing-element" type="text" placeholder="Address line 1" />
      </div>
      <div class="rf-billing-field">
        <input class="rf-billing-element" type="text" placeholder="Address line 2" />
      </div>
      <div class="rf-billing-row">
        <div class="rf-billing-field">
          <input class="rf-billing-element" type="text" placeholder="City" />
        </div>
        <div class="rf-billing-field">
          <input class="rf-billing-element" type="text" placeholder="State" />
        </div>
        <div class="rf-billing-field">
          <input class="rf-billing-element" type="text" placeholder="ZIP" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

defineProps(["actions"]);

const focusedField = ref(null);
</script>

<style lang="scss">
.rf-billing-form {
  width: 100%;
  max-width: 560px;
  padding-bottom: 16px;

  h2 {
    font-weight: 600;
    font-size: 16px;
    color: #333;
    margin-bottom: 8px;
    margin-top: 24px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  .rf-billing-row {
    display: flex;
    .rf-billing-field {
      margin-top: -1px;
    }
    .rf-billing-field + .rf-billing-field {
      margin-left: -1px;
    }
  }

  .rf-billing-field {
    flex: 1;
    + .rf-billing-field {
      margin-top: -1px;
    }
    .recurly-element-wrapper {
      width: 100%;
      height: 44px;
      box-sizing: border-box;
      position: relative;
    }
  }

  // Handle border radius for adjacent fields / rows
  .rf-billing-section {
    > .rf-billing-field:not(:first-child),
    > .rf-billing-row:not(:first-child) {
      .rf-billing-element,
      .recurly-element {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
    > .rf-billing-field:not(:last-child),
    > .rf-billing-row:not(:last-child) {
      .rf-billing-element,
      .recurly-element {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    .rf-billing-row .rf-billing-field:not(:last-child) {
      .rf-billing-element,
      .recurly-element {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    .rf-billing-row .rf-billing-field + .rf-billing-field {
      .rf-billing-element,
      .recurly-element {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }

  // Field styles - override with Custom CSS
  .rf-billing-element,
  .recurly-element {
    width: 100%;
    height: 44px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
    box-sizing: border-box;
    background: white;
    position: relative;
    outline: none;
    // [vs Brig] Pulse global CSS overrides text and placeholder color
    color: #333;
    &::placeholder {
      color: #999;
    }
  }

  // [vs Brig] in Brig this is inside Recurly iframe
  .recurly-hosted-field-input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    outline: none;
    font-size: 16px;
    padding: 0;
    color: #333;
    &::placeholder {
      color: #999;
    }
  }

  // Invalid styles - override with Custom CSS
  .rf-billing-element--invalid,
  .recurly-element-invalid {
    border-color: #c33 !important;
    position: relative;
    z-index: 1;
  }

  // Focus styles - override with Custom CSS
  .rf-billing-element:focus,
  .rf-billing-element:focus-visible,
  .recurly-element-focus {
    outline-offset: 2px;
    outline: 2px solid white;
    box-shadow: 0 0 0px 2px black;
    z-index: 1;
  }
}
</style>
